import type { MeResponse } from "@agw/contracts";
import { lazy, Suspense } from "react";
import { createBrowserRouter, Navigate } from "react-router";
import { canManagePeople, canSeeReports, canSubmit, isAdmin, isAgwStaff, isRep } from "../features/auth/useMe";
import { PageSpinner } from "../ui";
import { Layout } from "./Layout";
import { RequireAuth } from "./RequireAuth";
import { RouteError } from "./RouteError";

// Route-level code splitting: the scanner, the form and admin screens load on demand.
const LoginPage = lazy(() => import("../features/auth/LoginPage"));
const JobsListPage = lazy(() => import("../features/jobs/JobsListPage"));
const JobDetailPage = lazy(() => import("../features/jobs/JobDetailPage"));
const NewJobPage = lazy(() => import("../features/jobs/NewJobPage"));
const OpsPage = lazy(() => import("../features/ops/OpsPage"));
const PeoplePage = lazy(() => import("../features/people/PeoplePage"));
const ReportsPage = lazy(() => import("../features/reports/ReportsPage"));
const SettingsPage = lazy(() => import("../features/settings/SettingsPage"));
const InstallHelpPage = lazy(() => import("../features/help/InstallHelpPage"));
const QrPage = lazy(() => import("../features/help/QrPage"));
const ServedZipsPage = lazy(() => import("../features/zips/ServedZipsPage"));

const s = (el: React.ReactNode) => <Suspense fallback={<PageSpinner />}>{el}</Suspense>;

/** Reps land on their claims; everyone else on reports. */
const home = (me: MeResponse) => (isRep(me) || !canSeeReports(me) ? "/jobs" : "/reports");

export const router = createBrowserRouter([
  { path: "/login", element: s(<LoginPage />), errorElement: <RouteError /> },
  {
    path: "/",
    element: <RequireAuth>{(me) => <Layout me={me} />}</RequireAuth>,
    errorElement: <RouteError />,
    children: [
      {
        // Pathless: a crashed screen shows inside the app shell, menu still there.
        errorElement: <RouteError />,
        children: [
          { index: true, element: <RequireAuth>{(me) => <Navigate to={home(me)} replace />}</RequireAuth> },
          { path: "jobs", element: <RequireAuth>{(me) => s(<JobsListPage me={me} />)}</RequireAuth> },
          { path: "jobs/new", element: <RequireAuth allow={canSubmit}>{(me) => s(<NewJobPage me={me} />)}</RequireAuth> },
          { path: "jobs/:jobId", element: <RequireAuth>{(me) => s(<JobDetailPage me={me} />)}</RequireAuth> },
          { path: "reports", element: <RequireAuth allow={canSeeReports}>{(me) => s(<ReportsPage me={me} />)}</RequireAuth> },
          { path: "people", element: <RequireAuth allow={canManagePeople}>{(me) => s(<PeoplePage me={me} />)}</RequireAuth> },
          { path: "dealerships", element: <RequireAuth allow={canManagePeople}>{(me) => s(<PeoplePage me={me} tab="stores" />)}</RequireAuth> },
          { path: "settings", element: <RequireAuth>{(me) => s(<SettingsPage me={me} />)}</RequireAuth> },
          { path: "help/install", element: <RequireAuth>{() => s(<InstallHelpPage />)}</RequireAuth> },
          { path: "qr", element: <RequireAuth allow={isAdmin}>{() => s(<QrPage />)}</RequireAuth> },
          { path: "zips", element: <RequireAuth allow={isAdmin}>{() => s(<ServedZipsPage />)}</RequireAuth> },
          { path: "ops", element: <RequireAuth allow={isAgwStaff}>{(me) => s(<OpsPage me={me} />)}</RequireAuth> },
          { path: "*", element: <Navigate to="/" replace /> },
        ],
      },
    ],
  },
]);
